<script setup lang="ts">
import type { MarketingPreviewType } from '~/types/marketing'

const props = withDefaults(defineProps<{
  type: MarketingPreviewType
  src?: string
  alt: string
  caption?: string
  priority?: boolean
}>(), {
  src: undefined,
  caption: undefined,
  priority: false,
})

const imageFailed = ref(false)

watch(() => props.src, () => {
  imageFailed.value = false
})
</script>

<template>
  <figure class="overflow-hidden rounded-2xl bg-white shadow-lg shadow-slate-950/10 ring-1 ring-slate-950/10">
    <img
      v-if="src && !imageFailed"
      :src="src"
      :alt="alt"
      class="block h-auto w-full object-contain"
      :loading="priority ? 'eager' : 'lazy'"
      :fetchpriority="priority ? 'high' : 'auto'"
      @error="imageFailed = true"
    >

    <div v-else class="relative aspect-[16/10] overflow-hidden bg-slate-100 p-3 sm:p-5" role="img" :aria-label="alt">
      <div class="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
        <div class="flex h-9 shrink-0 items-center gap-1.5 border-b border-slate-200 bg-white px-4">
          <span class="h-2 w-2 rounded-full bg-red-300" />
          <span class="h-2 w-2 rounded-full bg-amber-300" />
          <span class="h-2 w-2 rounded-full bg-green-300" />
          <div class="ml-3 h-2 w-24 rounded-full bg-slate-100" />
          <span class="ml-auto text-[8px] font-semibold uppercase tracking-wider text-slate-400">Aperçu illustratif</span>
        </div>

        <div v-if="type === 'decision'" class="grid flex-1 grid-cols-[30%_1fr] bg-slate-100">
          <div class="hidden border-r border-slate-200 bg-sky-blue-900 p-4 sm:block">
            <div class="mb-5 h-4 w-20 rounded bg-white/25" />
            <div v-for="width in ['80%', '65%', '72%', '58%']" :key="width" class="mb-3 h-2.5 rounded bg-white/15" :style="{ width }" />
          </div>
          <div class="col-span-2 p-4 sm:col-span-1 sm:p-6">
            <p class="text-[9px] font-bold uppercase tracking-wider text-sky-blue-700">Action à renforcer</p>
            <div class="mt-2 flex items-center gap-2">
              <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-blue-100 text-[10px] font-bold text-sky-blue-800">A</div>
              <div>
                <div class="h-2.5 w-24 rounded bg-slate-800" />
                <div class="mt-1.5 h-1.5 w-16 rounded bg-slate-300" />
              </div>
              <div class="ml-auto rounded-lg bg-green-100 px-2 py-1 text-[9px] font-bold text-green-700">Scénario favorable</div>
            </div>
            <div class="mt-5 grid grid-cols-3 gap-2">
              <div v-for="label in ['Qualité', 'Valorisation', 'Allocation']" :key="label" class="rounded-lg border border-slate-200 bg-white p-2">
                <div class="h-1.5 w-8 rounded bg-slate-200" />
                <p class="mt-2 text-[9px] font-semibold text-slate-700">{{ label }}</p>
              </div>
            </div>
            <div class="mt-4 h-12 rounded-lg bg-gradient-to-r from-sky-blue-100 via-sky-blue-200 to-green-100 p-2">
              <div class="h-1.5 w-1/3 rounded bg-sky-blue-500/50" />
              <div class="mt-2 h-1.5 w-2/3 rounded bg-white/80" />
            </div>
          </div>
        </div>

        <div v-else-if="type === 'discover'" class="flex-1 p-3 sm:p-5">
          <div class="mb-3 flex gap-2">
            <span v-for="filter in ['Score Trident', 'Croissance', 'Valorisation']" :key="filter" class="rounded-full bg-sky-blue-100 px-2 py-1 text-[8px] font-semibold text-sky-blue-800">{{ filter }}</span>
          </div>
          <div class="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div class="grid grid-cols-[1.5fr_repeat(3,1fr)] bg-slate-50 px-3 py-2 text-[8px] font-bold uppercase text-slate-400">
              <span>Entreprise</span><span>Score</span><span>Croissance</span><span>Rang</span>
            </div>
            <div v-for="(score, index) in [18, 17, 16, 15]" :key="score" class="grid grid-cols-[1.5fr_repeat(3,1fr)] items-center border-t border-slate-100 px-3 py-2.5 text-[9px] text-slate-600">
              <span class="flex items-center gap-2 font-semibold text-slate-800"><i class="h-4 w-4 rounded bg-slate-100" />Entreprise {{ index + 1 }}</span>
              <span>{{ score }}/20</span><span>{{ 8 + index * 2 }} %</span><span class="font-semibold text-green-700">Qualifiée</span>
            </div>
          </div>
        </div>

        <div v-else-if="type === 'analyze'" class="flex-1 p-4 sm:p-6">
          <div class="flex items-start justify-between">
            <div><div class="h-3 w-28 rounded bg-slate-800" /><div class="mt-2 h-2 w-20 rounded bg-slate-200" /></div>
            <div class="rounded-lg bg-green-100 px-2 py-1 text-[9px] font-bold text-green-700">18/20</div>
          </div>
          <svg class="mt-5 h-[55%] w-full" viewBox="0 0 500 170" fill="none" aria-hidden="true">
            <path d="M0 145C65 135 85 105 130 116C185 130 206 63 265 80C324 96 349 25 500 18" stroke="#318DE7" stroke-width="5" stroke-linecap="round" />
            <path d="M0 156C82 150 106 135 151 139C222 145 238 108 300 118C374 130 407 76 500 68" stroke="#6AA84F" stroke-width="3" stroke-dasharray="8 8" stroke-linecap="round" />
            <path d="M0 160H500" stroke="#CBD5E1" /><path d="M0 110H500" stroke="#E2E8F0" /><path d="M0 60H500" stroke="#E2E8F0" />
          </svg>
          <div class="flex gap-4 text-[8px] font-semibold text-slate-500"><span class="text-sky-blue-700">● Cours de l’action</span><span class="text-green-700">● Fondamental</span></div>
        </div>

        <div v-else class="flex-1 p-4 sm:p-6">
          <div class="flex items-center justify-between"><div><p class="text-[8px] uppercase text-slate-400">Progression</p><p class="mt-1 text-lg font-bold text-slate-900">Niveau 8</p></div><div class="h-12 w-12 rounded-full border-[6px] border-sky-blue-500 border-r-sky-blue-100" /></div>
          <div class="mt-5 h-2 overflow-hidden rounded-full bg-slate-200"><div class="h-full w-[68%] rounded-full bg-gradient-to-r from-sky-blue-500 to-green-cucumber-500" /></div>
          <div class="mt-5 grid grid-cols-3 gap-2"><div v-for="label in ['Capital', 'Dépôts', 'Revenus passifs']" :key="label" class="rounded-lg bg-white p-2 shadow-sm"><div class="h-2 w-1/2 rounded bg-slate-200" /><p class="mt-2 text-[8px] font-semibold text-slate-600">{{ label }}</p></div></div>
        </div>
      </div>
    </div>
    <figcaption v-if="caption" class="text-balance border-t border-slate-100 bg-white px-5 py-3.5 text-center text-sm leading-5 text-slate-600">
      {{ caption }}
    </figcaption>
  </figure>
</template>
